// Per-clip settings dialog: rename, gain, fades, and one-click processing
// (normalize, reverse, clean voice, reduce noise, analyze tempo).
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { el, gainToDb, fmtTimecode, parseTimecode } from "../util";
import { openModal, toast } from "./modal";
import { openGenerateDialog } from "./generate-dialog";
import {
  clipPeak, linToDb, normalizeClip, reverseClip, cleanVoiceClip, reduceNoiseClip, analyzeTempo,
  makeMonoClip, trimSilenceClip, stretchToTempoClip, clipQuietestMono, reduceNoiseProfileClip,
} from "../audio/clipfx";
import { getNoiseProfile, setNoiseProfile, buildNoiseProfile, captureNoiseFromMic } from "../audio/noise-reduction";
import { IDENTITY_TRANSFORM, clipSpeed, sourceSpan } from "../state/model";

/** Open the clip settings as a modal dialog. */
export function openClipInspector(store: Store, engine: AudioEngine, clipId: string) {
  if (!store.findClip(clipId)) return;
  const content = el("div", { class: "fx-editor" });
  buildClipInspector(store, engine, clipId, content);
  const n = store.selectedClipIds.size;
  openModal(n > 1 ? `Clip settings — ${n} clips` : "Clip settings", content, [{ label: "Done", primary: true, onClick: () => {} }]);
}

/** Speed / duration dialog (Ctrl+R). Linked clips change together. */
export function openSpeedDialog(store: Store, clipId: string) {
  const f = store.findClip(clipId);
  if (!f) return;
  const cur = clipSpeed(f.clip);
  const pctIn = el("input", { attrs: { type: "number", min: "10", max: "1600", step: "5" } }) as HTMLInputElement;
  pctIn.value = String(Math.round(cur * 100));
  pctIn.style.width = "90px";
  const durOut = el("span", { class: "hint" });
  const span = sourceSpan(f.clip);
  const upd = () => { const s = Number(pctIn.value) / 100; durOut.textContent = s > 0 ? `New duration: ${fmtTimecode(span / s, store.project.fps)}` : ""; };
  pctIn.addEventListener("input", upd); upd();
  const presets = el("div", { class: "clip-actions" });
  for (const p of [25, 50, 75, 100, 150, 200, 400]) {
    const b = el("button", { text: `${p}%` });
    b.addEventListener("click", () => { pctIn.value = String(p); upd(); });
    presets.append(b);
  }
  const body = el("div", { class: "fx-editor" }, [
    el("div", { class: "fx-param" }, [el("label", { text: "Speed" }), pctIn, el("span", { text: "%" })]),
    presets, durOut,
    el("p", { class: "hint", text: "The clip keeps its start and media range; its length on the timeline changes. Linked audio/video change together. Audio pitch follows speed." }),
  ]);
  const apply = () => { const s = Number(pctIn.value) / 100; if (s > 0) store.setClipSpeed(clipId, s); };
  pctIn.addEventListener("keydown", (e) => { if (e.key === "Enter") { apply(); document.querySelector(".modal-bg")?.remove(); } });
  openModal("Speed / duration", body, [
    { label: "Cancel", onClick: () => {} },
    { label: "Apply", primary: true, onClick: apply },
  ]);
  setTimeout(() => { pctIn.focus(); pctIn.select(); }, 0);
}

/** Transition duration dialog for the cut at the head of `clipId`. */
export function openTransitionDialog(store: Store, clipId: string) {
  const f = store.findClip(clipId);
  if (!f) return;
  const fps = store.project.fps || 30;
  const video = f.track.kind === "video";
  const frames = el("input", { attrs: { type: "number", min: "2", step: "1" } }) as HTMLInputElement;
  frames.value = String(Math.round((f.clip.transitionIn || 1) * fps));
  frames.style.width = "80px";
  const secs = el("span", { class: "hint" });
  const upd = () => { secs.textContent = `= ${(Number(frames.value) / fps).toFixed(2)} s`; };
  frames.addEventListener("input", upd); upd();
  const presets = el("div", { class: "clip-actions" });
  for (const s of [0.25, 0.5, 1, 2, 3]) {
    const b = el("button", { text: `${s}s` });
    b.addEventListener("click", () => { frames.value = String(Math.round(s * fps)); upd(); });
    presets.append(b);
  }
  const body = el("div", { class: "fx-editor" }, [
    el("div", { class: "fx-param" }, [el("label", { text: "Duration" }), frames, el("span", { text: "frames" })]),
    secs, presets,
    el("p", { class: "hint", text: video
      ? "Centered on the cut. If a clip runs out of media inside the dissolve, its first/last frame is held. Linked audio gets a matching crossfade."
      : "Centered on the cut; uses the extra media each clip has beyond the cut (a side with none fades from the cut)." }),
  ]);
  const apply = () => { const n = Number(frames.value); if (n >= 2) store.setTransition(clipId, n / fps); };
  frames.addEventListener("keydown", (e) => { if (e.key === "Enter") { apply(); document.querySelector(".modal-bg")?.remove(); } });
  openModal(video ? "Cross dissolve" : "Crossfade", body, [
    { label: "Remove", onClick: () => { store.setTransition(clipId, undefined); } },
    { label: "Apply", primary: true, onClick: apply },
  ]);
  setTimeout(() => { frames.focus(); frames.select(); }, 0);
}

/** Render the clip settings into `content` (used by the docked Inspector and the
 *  modal). Returns a function that re-renders it. */
export function buildClipInspector(store: Store, engine: AudioEngine, clipId: string, content: HTMLElement): () => void {
  let busy = false;
  let nrStrength = 0.7;

  // --- noise reduction: sample a profile (mic or clip), then subtract it ---
  const sampleNoiseFromMic = async () => {
    if (busy) return; busy = true;
    toast("Capturing ~1.5 s of ambient noise — stay quiet…", 2500);
    try {
      const mono = await captureNoiseFromMic(engine.ctx, { seconds: 1.5 });
      setNoiseProfile(buildNoiseProfile(mono, engine.ctx.sampleRate, "live mic"));
      engine.syncGraph(); // push the new profile to any live Denoise inserts
      toast("Noise profile captured from mic.");
    } catch (e: any) { console.error(e); toast("Mic capture failed: " + (e?.message || e), 6000); }
    busy = false; render();
  };
  const sampleNoiseFromClip = () => {
    if (busy) return;
    const f = store.findClip(clipId); if (!f) return;
    const buf = store.buffers.get(f.clip.assetId);
    const mono = clipQuietestMono(store, f.clip);
    if (!mono || !buf) { toast("No audio to sample."); return; }
    setNoiseProfile(buildNoiseProfile(mono, buf.sampleRate, "this clip's quiet part"));
    engine.syncGraph(); // push the new profile to any live Denoise inserts
    toast("Noise profile captured from this clip's quietest part.");
    render();
  };
  const applyNoiseProfile = () => {
    const prof = getNoiseProfile();
    if (!prof) { toast("Sample a noise profile first — from the mic or a quiet part of a clip."); return; }
    run("Reduce noise", (c) => reduceNoiseProfileClip(store, engine, c, prof.mag, nrStrength));
  };

  // The action targets are all selected clips (so actions apply to the whole
  // selection), falling back to the clip the inspector was opened on.
  const targets = () => {
    const list = store.selectedClipList().map((x) => x.clip);
    if (list.length) return list;
    const f = store.findClip(clipId);
    return f ? [f.clip] : [];
  };

  // Run a per-clip operation across every target clip as one batch.
  const run = async (label: string, perClip: (clip: any) => any) => {
    if (busy) return;
    const clips = targets();
    if (!clips.length) return;
    busy = true; toast(`${label}${clips.length > 1 ? ` (${clips.length} clips)` : ""}…`, 4000);
    try { for (const c of clips) await perClip(c); toast(`${label} done.`); }
    catch (e) { console.error(e); toast(`${label} failed.`); }
    busy = false; render();
  };

  function render() {
    const f = store.findClip(clipId);
    if (!f) { content.innerHTML = ""; return; }
    const clip = f.clip;
    content.innerHTML = "";

    // name
    const name = el("input", { attrs: { value: clip.name } }) as HTMLInputElement;
    name.value = clip.name;
    name.addEventListener("change", () => store.updateClip(clipId, { name: name.value }));
    content.append(row("Name", name));

    // Generated media (title, color, bars…): edit its content right here.
    const genAsset = store.asset(clip.assetId);
    const owner = genAsset?.ownerId ? store.asset(genAsset.ownerId) : genAsset;
    if (owner?.generator) {
      const isText = owner.generator.type === "text";
      content.append(el("div", { class: "clip-actions" }, [
        actionBtn(isText ? "✎ Edit text & style…" : "✎ Edit item…", () => openGenerateDialog(store, { editAssetId: owner.id })),
        el("span", { class: "hint", text: "Changes every clip that uses it." }),
      ]));
    }

    const isVideo = f.track.kind === "video";
    const fps = store.project.fps;
    // Edits target ONLY the inspected clip, so a linked clip's volume/transform is
    // independent of its partners. Sliders take one undo step per grab.
    const applyAll = (patch: any) => store.updateClip(clipId, patch, false);
    const undoable = (s: HTMLInputElement) => { s.addEventListener("pointerdown", () => store.pushUndo()); s.addEventListener("keydown", () => store.pushUndo()); return s; };

    // ---- timing: start / duration / speed / enabled ----
    const tcInput = (sec: number) => { const i = el("input", { class: "tc-in" }) as HTMLInputElement; i.value = fmtTimecode(sec, fps); return i; };
    const startIn = tcInput(clip.start);
    startIn.title = "Start on the timeline (HH:MM:SS:FF, MM:SS or seconds). Linked clips move with it.";
    startIn.addEventListener("change", () => {
      const t = parseTimecode(startIn.value, fps);
      if (t == null) { startIn.value = fmtTimecode(clip.start, fps); return; }
      store.nudgeClips(t - clip.start, [clipId]);
    });
    const durIn = tcInput(clip.duration);
    durIn.title = "Length on the timeline (trims the end; can't exceed the media).";
    durIn.addEventListener("change", () => {
      const d = parseTimecode(durIn.value, fps);
      if (d == null || d <= 0) { durIn.value = fmtTimecode(clip.duration, fps); return; }
      store.pushUndo();
      store.trimClip(clipId, "right", clip.start + d);
    });
    const sp = clipSpeed(clip);
    const speedBtn = el("button", { text: `${Math.round(sp * 100)}%`, attrs: { title: "Change speed (Ctrl+R)" } });
    speedBtn.addEventListener("click", () => openSpeedDialog(store, clipId));
    const enabled = el("input", { attrs: { type: "checkbox" } }) as HTMLInputElement;
    enabled.checked = !clip.disabled;
    enabled.addEventListener("change", () => store.toggleClipsEnabled([clipId]));
    content.append(
      el("div", { class: "insp-timing" }, [
        el("label", { text: "Start" }), startIn,
        el("label", { text: "Duration" }), durIn,
        el("label", { text: "Speed" }), speedBtn,
        el("label", { text: "Enabled" }), el("span", {}, [enabled]),
      ]),
    );

    // Volume — audio clips only (video clips carry no audio).
    if (!isVideo) {
      const peak = clipPeak(store, clip);
      const gain = undoable(slider(-24, 24, 0.5, gainToDb(clip.gain)));
      const gOut = el("output", { text: fmtDb(gainToDb(clip.gain)) });
      gain.addEventListener("input", () => {
        const db = Number(gain.value); gOut.textContent = fmtDb(db);
        applyAll({ gain: db <= -24 ? 0 : Math.pow(10, db / 20) });
      });
      content.append(row("Volume", gain, gOut));
      content.append(el("p", { class: "hint", text: `Region peak: ${peak > 0 ? fmtDb(linToDb(peak)) : "—"}` }));
    }
    // Fades: audio = volume ramp; video = dissolve from/to black (opacity).
    {
      const maxF = Math.max(0.1, Math.min(10, clip.duration / 2));
      const fadeRow = (label: string, key: "fadeIn" | "fadeOut") => {
        const s = undoable(slider(0, maxF, 0.01, Math.min(maxF, clip[key])));
        const out = el("output", { text: clip[key].toFixed(2) + "s" });
        s.addEventListener("input", () => { out.textContent = Number(s.value).toFixed(2) + "s"; applyAll({ [key]: Number(s.value) }); });
        return row(label, s, out);
      };
      content.append(fadeRow(isVideo ? "Fade in (video)" : "Fade in", "fadeIn"), fadeRow(isVideo ? "Fade out (video)" : "Fade out", "fadeOut"));
      content.append(el("p", { class: "hint", text: "Tip: drag the small squares at a clip's top corners on the timeline to set fades." }));
    }

    // ---- video transform (position / scale / rotation / opacity / crop) ----
    if (isVideo) {
      const t = { ...IDENTITY_TRANSFORM, ...(clip.transform ?? {}) };
      const c = { t: 0, r: 0, b: 0, l: 0, ...(clip.crop ?? {}) };
      const pct = (v: number) => `${Math.round(v * 100)}%`;
      const setT = (k: keyof typeof t, v: number) => { (t as any)[k] = v; applyAll({ transform: { ...t } }); };
      const setC = (k: keyof typeof c, v: number) => { (c as any)[k] = v; const any = c.t || c.r || c.b || c.l; applyAll({ crop: any ? { ...c } : undefined }); };

      // labeled slider with a live readout
      const tRow = (label: string, min: number, max: number, step: number, val: number, fmt: (v: number) => string, on: (v: number) => void) => {
        const s = undoable(slider(min, max, step, val));
        const out = el("output", { text: fmt(val) });
        s.addEventListener("input", () => { const v = Number(s.value); out.textContent = fmt(v); on(v); });
        return row(label, s, out);
      };

      const panel = el("div", { class: "xform-panel" }, [
        el("div", { class: "xform-head", text: "🎞 Transform" }),
        tRow("Position X", -1, 1, 0.005, t.x, pct, (v) => setT("x", v)),
        tRow("Position Y", -1, 1, 0.005, t.y, pct, (v) => setT("y", v)),
        tRow("Scale", 0.1, 4, 0.01, t.scale, (v) => `${Math.round(v * 100)}%`, (v) => setT("scale", v)),
        tRow("Rotation", -180, 180, 1, t.rotation, (v) => `${Math.round(v)}°`, (v) => setT("rotation", v)),
        tRow("Opacity", 0, 1, 0.01, t.opacity, pct, (v) => setT("opacity", v)),
        el("div", { class: "xform-head", text: "Crop" }),
        tRow("Top", 0, 0.9, 0.01, c.t, pct, (v) => setC("t", v)),
        tRow("Right", 0, 0.9, 0.01, c.r, pct, (v) => setC("r", v)),
        tRow("Bottom", 0, 0.9, 0.01, c.b, pct, (v) => setC("b", v)),
        tRow("Left", 0, 0.9, 0.01, c.l, pct, (v) => setC("l", v)),
        el("div", { class: "clip-actions" }, [
          actionBtn("↺ Reset transform", () => { store.updateClip(clipId, { transform: { ...IDENTITY_TRANSFORM }, crop: undefined }); render(); }),
        ]),
      ]);
      content.append(panel);
    }

    // Audio-processing tools only apply to audio material — hide for video clips.
    if (isVideo) return;

    // actions
    const actions = el("div", { class: "clip-actions" });
    actions.append(
      actionBtn("Normalize", () => run("Normalize", (c) => { normalizeClip(store, c); })),
      actionBtn("Reverse", () => run("Reverse", (c) => reverseClip(store, engine, c))),
      actionBtn("Make mono", () => run("Make mono", (c) => makeMonoClip(store, engine, c))),
      actionBtn("Trim silence", () => run("Trim silence", (c) => { trimSilenceClip(store, c); })),
      actionBtn("Clean voice", () => run("Clean voice", (c) => cleanVoiceClip(store, engine, c))),
      actionBtn("Reduce noise", () => run("Reduce noise", (c) => reduceNoiseClip(store, engine, c))),
    );
    content.append(el("div", { class: "hint", text: "Processing (creates a new take; undoable):" }), actions);

    // Profile-based spectral noise reduction — grouped in one bordered panel so
    // it reads as a single 3-step effect process (sample → set strength → apply).
    const prof = getNoiseProfile();
    const nrStatus = el("span", {
      class: prof ? "badge live" : "badge",
      text: prof ? `Profile ready: ${prof.source}` : "No profile yet",
    });
    const strength = slider(0, 1, 0.05, nrStrength);
    const sOut = el("output", { text: `${Math.round(nrStrength * 100)}%` });
    strength.addEventListener("input", () => { nrStrength = Number(strength.value); sOut.textContent = `${Math.round(nrStrength * 100)}%`; });
    const applyBtn = actionBtn("Reduce noise", applyNoiseProfile);
    applyBtn.classList.add("nr-apply");
    if (!prof) applyBtn.setAttribute("disabled", "true");

    const nrPanel = el("div", { class: "nr-panel" }, [
      el("div", { class: "nr-head" }, [
        el("span", { class: "nr-title", text: "🔇 Profile Noise Reduction" }),
        nrStatus,
      ]),
      el("p", { class: "hint", text: "Removes steady background noise (hiss/hum) by sampling it, then subtracting that fingerprint from the recording." }),
      el("div", { class: "nr-step" }, [
        el("span", { class: "nr-num", text: "1" }),
        el("div", { class: "nr-body" }, [
          el("div", { class: "nr-label", text: "Sample a noise profile (a noise-only snippet)" }),
          el("div", { class: "clip-actions" }, [
            actionBtn("🎙 From mic", sampleNoiseFromMic),
            actionBtn("📋 From this clip", sampleNoiseFromClip),
          ]),
        ]),
      ]),
      el("div", { class: "nr-step" }, [
        el("span", { class: "nr-num", text: "2" }),
        el("div", { class: "nr-body" }, [
          el("div", { class: "nr-label", text: "Set strength" }),
          el("div", { class: "fx-param" }, [strength, sOut]),
        ]),
      ]),
      el("div", { class: "nr-step" }, [
        el("span", { class: "nr-num", text: "3" }),
        el("div", { class: "nr-body" }, [
          el("div", { class: "nr-label", text: "Apply to the selected clip(s)" }),
          el("div", { class: "clip-actions" }, [applyBtn]),
        ]),
      ]),
    ]);
    content.append(nrPanel);

    // analyze tempo
    const tempoBtn = actionBtn("Analyze tempo", () => {
      const bpm = analyzeTempo(store, clip);
      if (!bpm) { toast("Couldn't detect a tempo."); return; }
      tempoOut.textContent = `~${bpm} BPM`;
      applyTempo.style.display = "";
      applyTempo.onclick = () => { store.project.tempo = bpm; store.emitChange("project"); toast(`Project tempo set to ${bpm} BPM.`); };
    });
    const tempoOut = el("span", { class: "badge", text: "—" });
    const applyTempo = el("button", { class: "mini", text: "Set project tempo" });
    applyTempo.style.display = "none"; applyTempo.style.width = "auto";
    const matchBtn = actionBtn("Match project tempo", () => run("Time-stretch", (c) => stretchToTempoClip(store, engine, c)));
    content.append(el("div", { class: "clip-actions" }, [tempoBtn, tempoOut, applyTempo, matchBtn]));
  }

  render();
  return render;
}

function row(label: string, ...controls: HTMLElement[]): HTMLElement {
  return el("div", { class: "fx-param" }, [el("label", { text: label }), ...controls]);
}
function slider(min: number, max: number, step: number, value: number): HTMLInputElement {
  return el("input", { attrs: { type: "range", min: String(min), max: String(max), step: String(step), value: String(value) } }) as HTMLInputElement;
}
function actionBtn(label: string, on: () => void): HTMLButtonElement {
  const b = el("button", { text: label }); b.addEventListener("click", on); return b;
}
function fmtDb(db: number): string { return db === -Infinity ? "-∞ dB" : `${db >= 0 ? "+" : ""}${db.toFixed(1)} dB`; }
